import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import InputComponent from "@editor/components/common/designSystem/Input";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import { trpc, trpcClient, trpcUtils } from "@editor/utils/trpc";

import { errorToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { isValidDomain } from "replo-utils/lib/url";

import useEntriModal from "./useEntriModal";
import useSetupValidatedDomain from "./useSetupValidatedDomain";

interface DomainData {
  domain: string;
  id?: string;
  canBeDeleted: boolean;
  status: "verified" | "pending";
}

const renderDomainEntry = ({
  index,
  customDomain,
  customDomainId,
  canBeDeleted,
  status = "verified",
  onDeleteDomain,
}: {
  index: number;
  customDomain: string;
  customDomainId?: string;
  canBeDeleted: boolean;
  status?: "verified" | "pending";
  onDeleteDomain: (customDomainId: string) => void;
}) => {
  return (
    <div className="flex items-center">
      {/* NOTE (Cole, 2025-07-03): I set a static width here so numbers with multiple digits don't shift the layout */}
      <span className="text-base font-medium text-muted w-6">{index}</span>
      <div className="flex items-center justify-between w-full bg-sheet border-0.5 border-border gap-3 p-3 rounded-lg">
        <span className="text-base font-medium flex-1">{customDomain}</span>
        <div className="flex items-center gap-2">
          {canBeDeleted && customDomainId && (
            <IconButton
              variant="secondary"
              icon={<Trash2 size={12} />}
              size="sm"
              tooltipText="Remove domain"
              onClick={() => onDeleteDomain(customDomainId)}
            />
          )}
          {status === "pending" ? (
            <Tag variant="info" size="base">
              Pending
            </Tag>
          ) : (
            <Tag variant="success" size="base">
              Verified
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
};

export const Domains: React.FC<{
  isInputOpen: boolean;
  onOpenChange: (isInputOpen: boolean) => void;
  project: ReploProject;
}> = ({ isInputOpen, onOpenChange, project }) => {
  const { workspace } = useCurrentWorkspaceContext();

  // NOTE (Cole, 2025-06-02): JWT expires after an hour, may cause issues for edge case users
  const { data: entriData } = trpc.entri.fetchTokenAndApplicationId.useQuery(
    project?.id ? { projectId: project.id } : skipToken,
  );

  const { token, applicationId } = entriData ?? {};

  const [pendingDomain, setPendingDomain] = React.useState<string | null>(null);

  const { showEntriModal, isEntriModalInProgress } = useEntriModal({
    applicationId: applicationId ?? "",
    token: token ?? "",
    onSuccess: () => {
      setPendingDomain(null);
    },
    onError: () => {
      setPendingDomain(null);
    },
  });

  const { data: customDomainsData } =
    trpc.v2CustomDomain.getByProjectId.useQuery(
      project?.id ? { projectId: project.id } : skipToken,
    );

  const customDomains = customDomainsData?.customDomains ?? [];

  const { mutate: deleteCustomDomain } =
    trpc.v2CustomDomain.deleteCustomDomainInDatabase.useMutation({
      onMutate: async ({ projectId, customDomainId }) => {
        await trpcUtils.v2CustomDomain.getByProjectId.cancel({
          projectId,
        });

        const previousCustomDomains =
          trpcUtils.v2CustomDomain.getByProjectId.getData({
            projectId,
          });

        if (previousCustomDomains) {
          trpcUtils.v2CustomDomain.getByProjectId.setData(
            { projectId },
            {
              customDomains: previousCustomDomains.customDomains.filter(
                (domain) => domain.id !== customDomainId,
              ),
            },
          );
        }

        return { previousCustomDomains };
      },
      onError: (_err, { projectId }, context) => {
        if (context?.previousCustomDomains) {
          trpcUtils.v2CustomDomain.getByProjectId.setData(
            { projectId },
            context.previousCustomDomains,
          );
        }
      },
      onSettled: (_data, _error, { projectId }) => {
        void trpcUtils.v2CustomDomain.getByProjectId.invalidate({
          projectId,
        });
      },
    });

  const { setupValidatedDomain, isValidatedDomainSetupInProgress } =
    useSetupValidatedDomain();

  const [domainInput, setDomainInput] = React.useState({
    value: "",
    isValid: false,
  });

  const domains: DomainData[] = [
    ...(project?.shopDomainPrefix
      ? [
          {
            domain: `${project.shopDomainPrefix}.reploshops.com`,
            canBeDeleted: false,
            status: "verified" as const,
          },
        ]
      : []),
    ...customDomains.map((domain) => ({
      domain: domain.value,
      id: domain.id,
      canBeDeleted: true,
      status: "verified" as const,
    })),
    ...(pendingDomain
      ? [
          {
            domain: pendingDomain,
            canBeDeleted: false,
            status: "pending" as const,
          },
        ]
      : []),
  ];

  if (!workspace) {
    return null;
  }

  const handleAddDomain = async () => {
    if (token && applicationId && domainInput.isValid) {
      setPendingDomain(domainInput.value);
      setDomainInput({ value: "", isValid: false });
      onOpenChange(false);

      const { shopDomainPrefix } =
        await trpcClient.v2CustomDomain.throwIfCustomDomainAlreadyInDatabase.query(
          {
            projectId: project.id,
            customDomain: domainInput.value,
            workspaceId: workspace.id,
          },
        );

      if (!shopDomainPrefix) {
        errorToast(
          "Domain Setup Failed",
          "Unable to retrieve shop domain information. Please try again.",
        );
        return;
      }

      const { ok: cnameRecordIsAlreadySet } =
        await trpcUtils.v2CustomDomain.checkCustomDomainForReploshopsCname.fetch(
          {
            projectId: project.id,
            customDomain: domainInput.value,
          },
        );

      // NOTE (Cole, 2025-07-17): If the CNAME record is already set, we can skip Entri entirely.
      if (cnameRecordIsAlreadySet) {
        await setupValidatedDomain(domainInput.value);
        setPendingDomain(null);
      } else {
        showEntriModal(shopDomainPrefix, domainInput.value);
      }
    }
  };

  const handleDeleteDomain = (customDomainId: string) => {
    deleteCustomDomain({
      projectId: project.id,
      customDomainId,
    });
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex flex-col gap-2">
        {domains.map((domainData, index) => (
          <React.Fragment key={domainData.domain}>
            {renderDomainEntry({
              index: index + 1,
              customDomain: domainData.domain,
              customDomainId: domainData.id,
              canBeDeleted: domainData.canBeDeleted,
              status: domainData.status,
              onDeleteDomain: handleDeleteDomain,
            })}
          </React.Fragment>
        ))}

        {isInputOpen && (
          <div className="flex items-center">
            <span className="text-base font-medium text-muted w-6">
              {domains.length + 1}
            </span>
            <div className="flex flex-col w-full gap-3 py-3 rounded-lg">
              <div className="flex items-center justify-between w-full gap-3">
                <div className="flex-1 relative">
                  <InputComponent
                    size="base"
                    type="text"
                    placeholder="shop.example.com"
                    maxLength={256}
                    value={domainInput.value}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setDomainInput({
                        value,
                        isValid: isValidDomain(value),
                      });
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        token &&
                        applicationId &&
                        domainInput.isValid
                      ) {
                        void handleAddDomain();
                      }
                    }}
                    validityState={
                      domainInput.value && !domainInput.isValid
                        ? "invalid"
                        : "valid"
                    }
                  />
                  {domainInput.value && !domainInput.isValid && (
                    <div className="absolute top-full left-0 text-sm text-danger mt-1">
                      Please enter a valid domain (e.g., shop.example.com)
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="base"
                    onClick={() => {
                      onOpenChange(false);
                      setDomainInput({ value: "", isValid: false });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="base"
                    onClick={() => void handleAddDomain()}
                    disabled={
                      !Boolean(token && applicationId && domainInput.isValid)
                    }
                    isLoading={
                      isEntriModalInProgress || isValidatedDomainSetupInProgress
                    }
                  >
                    Connect
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
